import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useSession } from "../../auth/session"
import Api from "../../api/api"
import Spinner from "../widgets/Spinner"

type IlokLicenseRow = {
  ilokId: string, licenseGuid: string, productGuid: string, productName: string,
  licenseType: string, status: string, eligible: boolean,
  sku: { id: string, name: string, imageUrl: string | null } | null,
  claimed: boolean, deposited: boolean,
}

// The iLok page: everything the user's linked iLok accounts currently hold,
// fetched live from PACE via the backend on every visit. Purely
// informational — native licenses for eligible, mapped iLok licenses are
// issued automatically (at link time and by this very fetch), so the page
// just reports each license's state.
export default function IlokPage() {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const [licenses, setLicenses] = useState<IlokLicenseRow[] | undefined>(undefined)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    // empty on a hard reload before auth resolves
    if (!accessToken) return
    let cancelled = false
    Api.getIlokLicenses({ accessToken })
      .then(({ ilokLicenses }) => { if (!cancelled) setLicenses(ilokLicenses) })
      .catch(() => { if (!cancelled) setLoadError(true) })
    return () => { cancelled = true }
  }, [accessToken])

  return (
    <div className="page">
      <h1 className="page-title">iLok Licenses</h1>
      <p className="offers-section-subtitle">
        The licenses in your linked iLok accounts. Native licenses for
        matching Newfangled Audio products are added to your account
        automatically — find them in your <Link to="/licenses">Licenses</Link>.
      </p>

      {loadError && (
        <div className="empty-state-block">
          <p>Error loading your iLok licenses — try again in a minute.</p>
        </div>
      )}

      {!loadError && licenses === undefined && (
        <p className="empty-state"><Spinner size={16} /> Loading your iLok licenses…</p>
      )}

      {licenses !== undefined && licenses.length === 0 && (
        <div className="empty-state-block">
          <p>No iLok licenses found.</p>
          <p className="empty-state-hint">
            Link your iLok account on the <Link to="/account">Account page</Link> first —
            its licenses will show up here.
          </p>
        </div>
      )}

      {licenses !== undefined && licenses.length > 0 && (
        <div className="store-grid">
          {licenses.map((row) => (
            <div key={row.licenseGuid} className="store-card">
              {row.sku?.imageUrl
                ? <img src={row.sku.imageUrl} alt={row.sku.name} className="store-card-img" />
                : <div className="ilok-card-img-placeholder" />}
              <div className="store-card-body">
                <div className="store-card-name">{row.productName}</div>
                <p className="store-card-desc">
                  iLok {formatLicenseType(row.licenseType)} · {row.ilokId}
                </p>
                <div className="store-card-footer">
                  {row.deposited && <span className="badge">from your Newfangled Audio license</span>}
                  {!row.deposited && row.claimed && <span className="badge">native license issued</span>}
                  {!row.deposited && !row.claimed && row.sku === null && (
                    <span className="empty-state">no matching product</span>
                  )}
                  {!row.deposited && !row.claimed && row.sku !== null && (
                    <span className="empty-state">{row.eligible ? "not issued yet" : "not eligible"}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function formatLicenseType(licenseType: string): string {
  const labels: { [key: string]: string } = {
    FULL: "perpetual license",
    NFR: "NFR license",
    TRIAL: "trial",
    TRIALEXTENSION: "trial extension",
    RENTAL: "rental",
    SUBSCRIPTION: "subscription",
  }
  return labels[licenseType] ?? licenseType.toLowerCase() + " license"
}
